import LoadingSpinner from '@common/src/components/LoadingSpinner';
import { yupResolver } from '@hookform/resolvers/yup';
import { Party } from '@parties/src/types';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Flex,
  Text,
  Collapse,
  Input,
  PhoneInput,
  DateInput,
} from '@wegroup/design-system';
import cogoToast from 'cogo-toast';
import { FC, Suspense, useState, type ComponentProps } from 'react';
import {
  FormProvider,
  useForm,
  useFormContext,
  Controller,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import PartyCreationFormSection from '@parties/src/features/creation/components/PartyCreationFormSection';
import PartyCreationFormInput from '@parties/src/features/creation/components/PartyCreationFormInput';
import { Change } from '../../../../../../../../../types/Form';
import { createYellowhiveContactPerson } from 'wg-api-sdk';
import { wgApiSdk } from '../../../../../../../../app/src/features/api/wgApi';
import useAuth from '../../../../../../../../app/src/features/authentication/hooks/useAuth';
import { CountryCode } from 'libphonenumber-js/core';
import { queryClient } from '../../../../../../../../app/src/providers/queryClient';
import { getUsePartyQKey } from '@parties/src/features/detail/api/getParty';
import { getUsePartiesQKey } from '@parties/src/features/overview/api/getParties';

// Validation schema for YellowHive party creation
const YellowHivePartyCreationSchema = yup.object().shape({
  first_name: yup.string().required('REQUIRED_FIELD'),
  last_name: yup.string().required('REQUIRED_FIELD'),
  prefix_last_name: yup.string().optional(),
  telephonenr: yup.string().required('REQUIRED_FIELD'),
  email: yup.string().email('INVALID_EMAIL').required('REQUIRED_FIELD'),
  birth: yup.string().required('REQUIRED_FIELD'),
});

interface YellowHivePartyFormData {
  first_name: string;
  last_name: string;
  prefix_last_name?: string;
  telephonenr: string;
  email: string;
  birth: string;
}

interface Props {
  party: Party;
  sessionId: string;
  isOpen: boolean;
  onClose: () => void;
  onFinish: () => void;
}

// YellowHive Party Creation Form Component
const YellowHivePartyCreationForm: FC = () => {
  const { t } = useTranslation();
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<YellowHivePartyFormData>();
  const { distribution, user } = useAuth();
  type DSInputProps = ComponentProps<typeof Input>;
  const firstNameRegister = register('first_name');
  const prefixLastNameRegister = register('prefix_last_name');
  const lastNameRegister = register('last_name');
  const emailRegister = register('email');

  return (
    <PartyCreationFormSection
      title={t('PARTIES_CREATION_DETAILS_PERSONAL-INFO')}
    >
      <PartyCreationFormInput
        label={t('FIRST-NAME')}
        error={errors?.first_name}
        isRequired
      >
        <Input {...(firstNameRegister as unknown as DSInputProps)} />
      </PartyCreationFormInput>

      <PartyCreationFormInput
        label={t('INFIX')}
        error={errors?.prefix_last_name}
      >
        <Input {...(prefixLastNameRegister as unknown as DSInputProps)} />
      </PartyCreationFormInput>

      <PartyCreationFormInput
        label={t('LAST-NAME')}
        isRequired
        error={errors?.last_name}
      >
        <Input {...(lastNameRegister as unknown as DSInputProps)} />
      </PartyCreationFormInput>

      <PartyCreationFormInput
        label={t('PHONE')}
        isRequired
        error={errors?.telephonenr}
      >
        <Controller
          name="telephonenr"
          control={control}
          render={({ field: { onChange, value, name } }) => (
            <PhoneInput
              name={name}
              value={value || ''}
              onChange={onChange}
              defaultCountry={
                ((distribution?.address?.country_code as CountryCode) ||
                  'BE') as CountryCode
              }
              language={user?.language?.toLowerCase() || 'nl'}
            />
          )}
        />
      </PartyCreationFormInput>

      <PartyCreationFormInput
        label={t('EMAIL')}
        isRequired
        error={errors?.email}
      >
        <Input {...(emailRegister as unknown as DSInputProps)} type="email" />
      </PartyCreationFormInput>

      <PartyCreationFormInput
        label={t('DATE-OF-BIRTH')}
        isRequired
        error={errors?.birth}
      >
        <Controller
          name="birth"
          control={control}
          render={({ field: { onChange, value } }) => (
            <DateInput
              onChange={({ value }: Change) => onChange(value)}
              initialValue={value}
            />
          )}
        />
      </PartyCreationFormInput>
    </PartyCreationFormSection>
  );
};

const getDefaultValues = (party: Party): YellowHivePartyFormData => {
  return {
    first_name: party.initials || '',
    last_name: party.last_name || '',
    prefix_last_name: party.infix || '',
    telephonenr: party.telephonenr || '',
    email: party.email || '',
    birth: party.birth || '',
  };
};

export const FinishBasketYellowHiveContactPersonModal = ({
  party,
  sessionId,
  isOpen,
  onClose,
  onFinish,
}: Props) => {
  const { t } = useTranslation();
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<YellowHivePartyFormData>({
    defaultValues: getDefaultValues(party),
    resolver: yupResolver(YellowHivePartyCreationSchema),
  });

  const handleSubmit = methods.handleSubmit(async (data) => {
    try {
      setIsSubmitting(true);
      setIsError(false);
      setError(null);

      await createYellowhiveContactPerson(
        {
          body: {
            first_name: data.first_name,
            last_name: data.last_name,
            prefix_last_name: data.prefix_last_name,
            telephonenr: data.telephonenr,
            email: data.email,
            // @ts-expect-error - date vs string
            birth: data.birth,
          },
          params: {
            path: {
              session_id: sessionId,
            },
            query: {},
          },
        },
        wgApiSdk,
      );

      methods.reset(getDefaultValues(party));
      cogoToast.success(t('PORTFOLIO_PARTY-CREATION_TOAST_SUCCESS'));

      const queryKey = getUsePartiesQKey();
      queryClient.invalidateQueries(queryKey);
      if (party.id) {
        const queryKeyParty = getUsePartyQKey(party.id);
        queryClient.invalidateQueries(queryKeyParty);
      }

      onClose();
      onFinish();
    } catch (err) {
      console.error('Error creating YellowHive party:', err);
      setIsError(true);
      setError(
        err instanceof Error ? err : new Error('ONBOARDING_ERROR_TITLE'),
      );
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: 'full', tablet: '2xl' }}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <FormProvider {...methods}>
        <ModalContent
          mx={{ base: '0', tablet: 'auto' }}
          my={{ base: '0', tablet: 'auto' }}
          maxH={{ base: '100vh', tablet: 'auto' }}
          borderRadius={{ base: '0', tablet: 'md' }}
        >
          <ModalHeader
            px={{ base: '4', tablet: '6' }}
            py={{ base: '4', tablet: '6' }}
          >
            <Flex
              justify="space-between"
              pr={{ base: '8', tablet: '8' }}
              align="center"
            >
              <Flex direction="column" gap="2">
                <Text fontSize={{ base: 'xl', tablet: 'lg' }} fontWeight="600">
                  {t(`YELLOWHIVE_FLOWS_CREATE_CONTACT-PERSON_MODAL_TITLE`)}
                </Text>
                <Text
                  fontSize={{ base: 'md', tablet: 'sm' }}
                  color="gray.600"
                  fontWeight="400"
                >
                  {t(`YELLOWHIVE_FLOWS_CREATE_CONTACT-PERSON_MODAL_SUBTITLE`)}
                </Text>
              </Flex>
            </Flex>
          </ModalHeader>
          <ModalCloseButton mt="2" />
          <ModalBody
            px={{ base: '4', tablet: '6' }}
            py={{ base: '0', tablet: '6' }}
          >
            <Collapse in={isError}>
              <Text color="red.500" p={4} bg="red.50" borderRadius="md" mb="4">
                {t(error?.message ?? 'ONBOARDING_ERROR_TITLE')}
              </Text>
            </Collapse>
            <Suspense fallback={<LoadingSpinner />}>
              <YellowHivePartyCreationForm />
            </Suspense>
          </ModalBody>
          <ModalFooter
            px={{ base: '4', tablet: '6' }}
            py={{ base: '6', tablet: '6' }}
            flexDirection={{ base: 'column', tablet: 'row' }}
            gap={{ base: '3', tablet: '0' }}
          >
            <Button
              variant="ghost"
              mr={{ base: '0', tablet: '3' }}
              onClick={onClose}
              w={{ base: 'full', tablet: 'auto' }}
              order={{ base: '2', tablet: '1' }}
            >
              {t('CLOSE')}
            </Button>
            <Button
              isDisabled={!methods.formState.isDirty}
              onClick={handleSubmit}
              isLoading={isSubmitting}
              w={{ base: 'full', tablet: 'auto' }}
              order={{ base: '1', tablet: '2' }}
            >
              {t('ANVA_FLOWS_CREATE_PARTY_MODAL_SUBMIT')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </FormProvider>
    </Modal>
  );
};
